import {
  Modal,
  Box,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
} from "@mui/material";
import { useState } from "react";
import useAppDispatch from "../../../hooks/useAppDispatch";
import { createProperty } from "../../../redux/slices/property";
import { AuthUser, User } from "../../../types/auth";

interface PropertyModalProps {
  show: boolean;
  onClose: () => void;
  property?: any;
  currentUser: AuthUser;
  users: User[];
  properties: any;
  area: any;
  initialName?: string;
}

const PropertyModal = ({
  show,
  onClose,
  property,
  currentUser,
  users,
  properties,
  area,
  initialName,
}: PropertyModalProps) => {
  const dispatch = useAppDispatch();
  const [selectedUserId, setSelectedUserId] = useState("");
  const [propertyName, setPropertyName] = useState(initialName ?? "");
  const [numberOfUnits, setNumberOfUnits] = useState("");
  const [unitizationMethod, setUnitizationMethod] = useState("easy");
  const roadIdToSend = currentUser?.activeAssociation.road.id;

  // Create unitization rows for easy mode (MUU category with Perusyksiköinti)
  const unitizationRows = numberOfUnits
    ? [
        {
          id: crypto.randomUUID(),
          trafficType: "muu",
          hectareCoefficient: "1",
          trafficWeightCoefficient: "0",
          weightCoefficient: "100",
          distance: "1",
          comments: "Perusyksiköinti",
          weightValue: parseFloat(numberOfUnits) || 0,
          kayttomaksuAmount: "0",
          muuTripCount: "1",
          muuVehicleWeight: numberOfUnits,
          muuPayload: "0",
        },
      ]
    : undefined;

  const formPayload = {
    name: propertyName,
    code: property?.properties?.kiinteistotunnuksenEsitysmuoto,
    address: "",
    units: Number(numberOfUnits),
    road: roadIdToSend,
    user: selectedUserId,
    area: area,
    unitizationRows,
    propertyGeometries: {
      geom: property?.geometry,
      location: {
        type: "Point",
        coordinates:
          property?.properties?.kiinteistotunnuksenSijainti?.coordinates,
      },
    },
  };

  return (
    <Modal open={show} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 500,
          bgcolor: "background.paper",
          borderRadius: 2,
          boxShadow: 24,
          p: 4,
        }}
      >
        <Typography variant="h6" mb={2}>
          Lisää kiinteistö tiekuntaan
        </Typography>

        <TextField
          fullWidth
          margin="normal"
          label="Kiinteistön nimi"
          value={propertyName}
          onChange={(e) => setPropertyName(e.target.value)}
        />

        <TextField
          fullWidth
          margin="normal"
          label="Kiinteistötunnus"
          value={property?.properties?.kiinteistotunnuksenEsitysmuoto}
          disabled
          InputProps={{
            readOnly: true,
          }}
        />

        <FormControl fullWidth margin="normal">
          <InputLabel id="osakas-label">Valitse osakas</InputLabel>
          <Select
            labelId="osakas-label"
            value={selectedUserId}
            label="Valitse osakas"
            onChange={(e) => setSelectedUserId(e.target.value)}
          >
            {users.map((osakas: any) => (
              <MenuItem key={osakas.id} value={osakas.id}>
                {`${osakas.firstName} ${osakas.lastName}`}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl component="fieldset" margin="normal">
          <Typography variant="body2">Yksiköintitapa</Typography>
          <RadioGroup
            row
            value={unitizationMethod}
            onChange={(e) => setUnitizationMethod(e.target.value)}
          >
            <FormControlLabel value="easy" control={<Radio />} label="Helppo" />
            <FormControlLabel
              value="advanced"
              control={<Radio />}
              label="Edistynyt"
              disabled
            />
          </RadioGroup>
        </FormControl>

        <TextField
          fullWidth
          margin="normal"
          label="Yksiköiden lukumäärä"
          type="number"
          value={numberOfUnits}
          onChange={(e) => setNumberOfUnits(e.target.value)}
        />

        <Box mt={3} display="flex" justifyContent="flex-end" gap={2}>
          <Button variant="outlined" onClick={onClose}>
            Sulje
          </Button>
          <Button
            onClick={async () => {
              try {
                await dispatch(createProperty(formPayload));
                onClose();
              } catch (error) {
                console.error("Propertyn lisääminen epäonnistui: ", error);
              }
            }}
            variant="contained"
            color="primary"
          >
            Lisää alue
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default PropertyModal;
